import { useMemo, useEffect, useState } from "react"
import { Card, Button, Space } from "antd"
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons"
import UserTable from "./components/UserTable"
import UserModal from "./components/UserModal"
import { useCrud } from "@/hooks/useCrud"
import { User, UserFormData } from "./types"
import userApi from "./api"
import { ID } from "@/types/common"
import roleApi from "../role/api"

const UserManagement = () => {
  const [roles, setRoles] = useState<Array<{ id: ID; name: string; code: string }>>([])

  // 加载角色列表（用于用户表单中的角色选择）
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await roleApi.getList()
        const roleList = res.data || []
        setRoles(
          roleList.map((role) => ({
            id: role.id,
            name: role.name,
            code: role.code,
          }))
        )
      } catch (error) {
        console.error("加载角色列表失败:", error)
      }
    }
    loadRoles()
  }, [])

  const api = useMemo(
    () => ({
      getList: () => userApi.getList(),
      create: userApi.create,
      update: (id: ID, data: UserFormData | Partial<UserFormData>) =>
        userApi.update(id, data as UserFormData),
      delete: userApi.delete,
    }),
    []
  )

  const {
    items: users,
    loading,
    opLoading,
    isModalOpen,
    editingItem: editingUser,
    selectedIds,
    setSelectedIds,
    handleAdd,
    handleEdit,
    handleSave,
    handleDelete,
    handleBatchDelete,
    handleCloseModal,
  } = useCrud<User, UserFormData>({
    api,
    entityName: "用户",
  })

  // 计算统计数据
  const totalUsers = users.length
  const selectedCount = selectedIds.length
  const batchDisabled = selectedCount === 0

  return (
    <div className="p-6">
      {/* 主要内容区域 */}
      <Card>
        {/* 操作栏 */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <span className="text-gray-500">共 {totalUsers} 名用户</span>
          </div>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              loading={opLoading}
              data-testid="btn-add-user"
            >
              新增用户
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={batchDisabled}
              loading={opLoading}
              data-testid="btn-batch-delete"
            >
              批量删除 {selectedCount > 0 && `(${selectedCount})`}
            </Button>
          </Space>
        </div>

        {/* 用户表格 */}
        <UserTable
          users={users}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </Card>

      {/* 用户信息模态框 */}
      <UserModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        user={editingUser}
        loading={opLoading}
        roles={roles}
      />
    </div>
  )
}

export default UserManagement
